import { createFileRoute, notFound } from "@tanstack/react-router"
import { PREORDER_ENABLED } from "@/lib/constants/site"
import { DEFAULT_OG_IMAGE, SITE_URL } from "@/lib/constants/site-url"
import {
  isCateringVisible,
  siteSettingsQueryOptions,
} from "@/lib/data/site-settings"
import { jsonLd } from "@/lib/utils/json-ld"
import FAQPage, { buildFaqSections } from "@/pages/faq"

const OG_IMAGE = DEFAULT_OG_IMAGE

export const Route = createFileRoute("/$countryCode/faq")({
  loader: async ({ params, context }) => {
    const { countryCode } = params
    const { queryClient } = context
    const { getRegion } = await import("@/lib/data/regions")

    const region = await queryClient.ensureQueryData({
      queryKey: ["region", countryCode],
      queryFn: () => getRegion({ country_code: countryCode }),
    })

    if (!region) {
      throw notFound()
    }

    // Same cached query the root loader prefetches and the page reads through
    // useCateringVisible, so the schema and the visible answers agree.
    const siteSettings = await queryClient.ensureQueryData(
      siteSettingsQueryOptions
    )

    return {
      countryCode,
      region,
      cateringVisible: isCateringVisible(siteSettings),
    }
  },
  head: ({ loaderData }) => {
    const title = PREORDER_ENABLED
      ? "FAQ | +Bake Cafe — Pre-Orders, Catering & More"
      : "FAQ | +Bake Cafe — Ordering, Catering & More"
    const description = PREORDER_ENABLED
      ? "Frequently asked questions about +Bake Cafe. Learn about pre-order pickup, catering policies, lead times, pickup windows, allergy info, and more."
      : "Frequently asked questions about +Bake Cafe. Learn about ordering on Toast for pickup or delivery, catering requests, allergy info, our coffee, hours, and more."
    const canonical = `${SITE_URL}/faq`

    // Generate the FAQPage schema from the same data the page renders so the
    // structured data always matches the visible Q&A (Google requires parity)
    // and answer engines ingest all questions, not a stale hardcoded subset.
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: buildFaqSections(
        loaderData?.cateringVisible ?? false
      ).flatMap((section) =>
        section.faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.a,
          },
        }))
      ),
    }

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "FAQ", item: canonical },
      ],
    }

    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: canonical },
        { property: "og:image", content: OG_IMAGE },
        { property: "og:site_name", content: "+Bake Cafe" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: OG_IMAGE },
      ],
      links: [{ rel: "canonical", href: canonical }],
      scripts: [
        { type: "application/ld+json", children: jsonLd(faqSchema) },
        { type: "application/ld+json", children: jsonLd(breadcrumbSchema) },
      ],
    }
  },
  component: FAQPage,
})
